import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { useActiveTenant } from '../contexts/tenant'
import type { Product } from '@wsfull/shared'

type Warehouse = { id: string; name: string; type: string; active: boolean }
type FifoLot = { lotId: string; lotNumber: string; receivedAt: string; quantity: number }
type StockBalance = { productId: string; locationId: string; quantity: number; locationCode: string | null }
type Location = {
  id: string; code: string; type: string; zone: string; active: boolean
  warehouseId: string | null; address: string | null
}
type PalletOption = {
  palletId: string; palletCode: string; locationId: string | null; locationCode: string | null
  warehouseId: string | null; lotId: string | null; lotNumber: string | null
  expiryDate: string | null; boxes: number; units: number; unitsPerBox: number | null; aberto: boolean
}
type ItemRow = {
  productId: string; lotId: string; fromLocationId: string; toLocationId: string
  quantityRequested: number
  // Origem em palete: caixa fechada. As unidades saem de caixas × unidades por caixa.
  source: 'pallet' | 'location'; palletId: string; boxesRequested: number
}

const emptyItem = (): ItemRow => ({
  productId: '', lotId: '', fromLocationId: '', toLocationId: '',
  quantityRequested: 1, source: 'pallet', palletId: '', boxesRequested: 1,
})

const statusLabel: Record<string, string> = { pending: 'Pendente', completed: 'Concluída', cancelled: 'Cancelada' }
const statusVariant: Record<string, 'default' | 'success' | 'danger'> = { pending: 'default', completed: 'success', cancelled: 'danger' }

export function TransfersPage() {
  const tenantId = useActiveTenant()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ fromWarehouseId: '', toWarehouseId: '', reason: '', notes: '' })
  const [items, setItems] = useState<ItemRow[]>([emptyItem()])

  const { data: transfers = [], isLoading } = useQuery<any[]>({
    queryKey: ['transfers', tenantId],
    queryFn: async () => {
      if (!tenantId) return []
      return (await api.get('/transfers', { params: { tenantId } })).data
    },
    enabled: !!tenantId,
  })

  const { data: warehouses = [] } = useQuery<Warehouse[]>({
    queryKey: ['warehouses'],
    queryFn: async () => (await api.get('/warehouses')).data,
  })

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const { data: balances = [] } = useQuery<StockBalance[]>({
    queryKey: ['stock', 'balance', tenantId],
    queryFn: async () => (await api.get('/stock/balance', { params: { tenantId } })).data,
    enabled: !!tenantId && showForm,
  })

  // Localizações do cadastro — o destino sai daqui, não do saldo: local de
  // fracionamento vazio precisa aparecer para poder receber a transferência.
  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['stock', 'locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
    enabled: showForm,
  })

  // Per-item FIFO lot query
  const [fifoMap, setFifoMap] = useState<Record<number, FifoLot[]>>({})
  // Paletes que têm o produto, na ordem de abertura (aberto > FEFO > menor qtd)
  const [palletMap, setPalletMap] = useState<Record<number, PalletOption[]>>({})

  async function loadFifoLots(idx: number, productId: string, locationId: string) {
    if (!productId || !locationId) return
    try {
      const res = await api.get('/lots/fifo', { params: { tenantId, productId, locationId } })
      setFifoMap(m => ({ ...m, [idx]: res.data }))
    } catch {
      setFifoMap(m => ({ ...m, [idx]: [] }))
    }
  }

  async function loadPallets(idx: number, productId: string) {
    if (!productId) return
    try {
      const res = await api.get('/pallets/with-product', { params: { tenantId, productId } })
      const list: PalletOption[] = res.data
      setPalletMap(m => ({ ...m, [idx]: list }))
      // Sem palete com o produto, a origem só pode ser localização.
      if (list.length === 0) setItem(idx, { source: 'location' })
    } catch {
      setPalletMap(m => ({ ...m, [idx]: [] }))
      setItem(idx, { source: 'location' })
    }
  }

  const locationsForProduct = (productId: string) =>
    balances.filter(b => b.productId === productId && b.quantity > 0)

  // Locais de um armazém. Se nenhum local estiver vinculado a ele, mostra todos —
  // melhor listar demais do que travar o operador num select vazio.
  const locationsOf = (warehouseId: string) => {
    const active = locations.filter(l => l.active)
    const own = active.filter(l => l.warehouseId === warehouseId)
    return { list: own.length > 0 ? own : active, unmapped: own.length === 0 && !!warehouseId }
  }
  // GERAL é criada em código (stock.service) e não tem armazém vinculado, mas é
  // ONDE o fracionamento realmente guarda estoque. Filtrar só por armazém a
  // deixava de fora e sobravam locais que ninguém usa.
  const generalLoc = locations.find(l => l.code === 'GERAL' && l.active)

  const destLocations = (() => {
    const base = locationsOf(form.toWarehouseId)
    if (!generalLoc || base.list.some(l => l.id === generalLoc.id)) return base
    return { list: [generalLoc, ...base.list], unmapped: false }
  })()
  const origLocations = locationsOf(form.fromWarehouseId)

  // Fracionamento (armazém de picking) recebe em GERAL — já vem escolhido.
  const defaultDestFor = (warehouseId: string) => {
    const w = warehouses.find(x => x.id === warehouseId)
    return w?.type === 'picking' && generalLoc ? generalLoc.id : ''
  }

  const locLabel = (l: Location) => l.code || l.address || l.id.slice(0, 8)

  const setItem = (idx: number, patch: Partial<ItemRow>) =>
    setItems(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r))

  const palletOf = (idx: number, palletId: string) => (palletMap[idx] ?? []).find(p => p.palletId === palletId)

  const itemReady = (i: ItemRow, idx: number) => {
    if (!i.productId || !i.toLocationId) return false
    if (i.source === 'pallet') {
      const p = palletOf(idx, i.palletId)
      // Palete sem lugar cadastrado: o operador informa de onde ele sai.
      return !!p && !!(p.locationId || i.fromLocationId) && i.boxesRequested > 0 && i.boxesRequested <= p.boxes
    }
    return !!i.fromLocationId && i.quantityRequested > 0
  }

  const create = useMutation({
    mutationFn: async () => api.post('/transfers', {
      tenantId,
      fromWarehouseId: form.fromWarehouseId,
      toWarehouseId: form.toWarehouseId,
      reason: form.reason,
      notes: form.notes || undefined,
      // O índice tem que ser o da linha na tela — é a chave do palletMap.
      // Filtrar antes de mapear reindexaria e pegaria o palete da linha errada.
      items: items.map((i, idx) => ({ i, idx })).filter(({ i, idx }) => itemReady(i, idx)).map(({ i, idx }) => {
        if (i.source === 'pallet') {
          const p = palletOf(idx, i.palletId)!
          return {
            productId: i.productId,
            palletId: i.palletId,
            boxesRequested: i.boxesRequested,
            // O servidor recalcula pelo cadastro; aqui é só o palpite da tela.
            quantityRequested: i.boxesRequested * (p.unitsPerBox ?? 1),
            fromLocationId: p.locationId ?? i.fromLocationId,
            toLocationId: i.toLocationId,
          }
        }
        return {
          productId: i.productId,
          lotId: i.lotId || undefined,
          fromLocationId: i.fromLocationId,
          toLocationId: i.toLocationId,
          quantityRequested: i.quantityRequested,
        }
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers', tenantId] })
      setShowForm(false)
      setForm({ fromWarehouseId: '', toWarehouseId: '', reason: '', notes: '' })
      setItems([emptyItem()])
      setFifoMap({})
      setPalletMap({})
    },
  })

  const canCreate = !!form.fromWarehouseId && !!form.toWarehouseId && !!form.reason &&
    items.some((i, idx) => itemReady(i, idx))

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Transferências</h2>
          <p className="text-sm text-gray-500">Movimentação entre armazéns com controle FIFO</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(true)}><Plus className="h-4 w-4" /> Nova transferência</Button>
      </div>

      <Modal title="Nova transferência" open={showForm} onClose={() => setShowForm(false)} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Armazém origem *</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.fromWarehouseId} onChange={e => setForm(p => ({ ...p, fromWarehouseId: e.target.value }))}>
                <option value="">Selecione...</option>
                {warehouses.filter(w => w.active).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Armazém destino *</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.toWarehouseId}
                onChange={e => {
                  const to = e.target.value
                  setForm(p => ({ ...p, toWarehouseId: to }))
                  const dflt = defaultDestFor(to)
                  setItems(prev => prev.map(r => ({ ...r, toLocationId: dflt })))
                }}>
                <option value="">Selecione...</option>
                {warehouses.filter(w => w.active && w.id !== form.fromWarehouseId).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Justificativa *</label>
            <input className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
              placeholder="Ex: Fracionamento para picking" />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Itens *</label>
              <Button variant="ghost" size="sm" onClick={() => setItems(p => [...p, { ...emptyItem(), toLocationId: defaultDestFor(form.toWarehouseId) }])}>
                <Plus className="h-4 w-4" /> Adicionar
              </Button>
            </div>
            <div className="space-y-3">
              {items.map((item, idx) => {
                const locs = locationsForProduct(item.productId)
                const fifoLots = fifoMap[idx] ?? []
                const palletOpts = palletMap[idx] ?? []
                const pallet = palletOf(idx, item.palletId)
                const upc = pallet?.unitsPerBox ?? 0
                return (
                  <div key={idx} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                    <div className="flex gap-2">
                      <select className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={item.productId}
                        onChange={e => {
                          setItem(idx, { productId: e.target.value, lotId: '', fromLocationId: '', toLocationId: '', palletId: '', boxesRequested: 1, source: 'pallet' })
                          setFifoMap(m => ({ ...m, [idx]: [] }))
                          setPalletMap(m => ({ ...m, [idx]: [] }))
                          loadPallets(idx, e.target.value)
                        }}>
                        <option value="">Produto...</option>
                        {products.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                      </select>
                      {items.length > 1 && <Button variant="ghost" size="sm" onClick={() => setItems(p => p.filter((_, i) => i !== idx))}>✕</Button>}
                    </div>

                    {item.productId && (
                      <>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="font-medium text-gray-700">Origem:</span>
                          <label className={`flex items-center gap-1 ${palletOpts.length === 0 ? 'text-gray-300' : 'text-gray-700'}`}>
                            <input type="radio" disabled={palletOpts.length === 0}
                              checked={item.source === 'pallet'}
                              onChange={() => setItem(idx, { source: 'pallet', fromLocationId: '', lotId: '' })} />
                            Palete {palletOpts.length > 0 && `(${palletOpts.length})`}
                          </label>
                          <label className="flex items-center gap-1 text-gray-700">
                            <input type="radio"
                              checked={item.source === 'location'}
                              onChange={() => setItem(idx, { source: 'location', palletId: '' })} />
                            Localização
                          </label>
                          {palletOpts.length === 0 && <span className="text-gray-400">nenhum palete com esse produto</span>}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          {item.source === 'pallet' ? (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-700">
                                Palete (★ = abrir primeiro)
                              </label>
                              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                value={item.palletId}
                                onChange={e => {
                                  const p = palletOf(idx, e.target.value)
                                  setItem(idx, {
                                    palletId: e.target.value,
                                    fromLocationId: p?.locationId ?? '',
                                    lotId: p?.lotId ?? '',
                                    boxesRequested: Math.min(item.boxesRequested || 1, p?.boxes ?? 1),
                                  })
                                }}>
                                <option value="">Selecione...</option>
                                {palletOpts.map((p, pi) => (
                                  <option key={p.palletId} value={p.palletId}>
                                    {pi === 0 ? '★ ' : ''}{p.palletCode} — {p.boxes} cx
                                    {p.lotNumber ? ` — lote ${p.lotNumber}` : ''}
                                    {p.expiryDate ? ` — val ${new Date(p.expiryDate).toLocaleDateString('pt-BR')}` : ''}
                                    {p.locationCode ? ` — ${p.locationCode}` : ' — SEM LOCAL'}
                                    {p.aberto ? ' — aberto' : ''}
                                  </option>
                                ))}
                              </select>
                              {pallet && !pallet.locationId && (
                                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2">
                                  <label className="mb-1 block text-xs font-medium text-amber-800">
                                    Palete sem localização — de onde ele sai?
                                  </label>
                                  <select className="w-full rounded-lg border border-amber-200 px-3 py-2 text-sm"
                                    value={item.fromLocationId}
                                    onChange={e => setItem(idx, { fromLocationId: e.target.value })}>
                                    <option value="">Selecione...</option>
                                    {origLocations.list.map(l => <option key={l.id} value={l.id}>{locLabel(l)}</option>)}
                                  </select>
                                  <p className="mt-1 text-xs text-amber-700">Fica gravado no palete — só perguntamos uma vez.</p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-700">Localização origem</label>
                              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                value={item.fromLocationId}
                                onChange={e => { setItem(idx, { fromLocationId: e.target.value, lotId: '' }); loadFifoLots(idx, item.productId, e.target.value) }}>
                                <option value="">Selecione...</option>
                                {locs.map(l => <option key={l.locationId} value={l.locationId}>{l.locationCode ?? '(sem código)'} — saldo: {l.quantity}</option>)}
                              </select>
                            </div>
                          )}

                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-700">Localização destino</label>
                            <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                              value={item.toLocationId} onChange={e => setItem(idx, { toLocationId: e.target.value })}>
                              <option value="">Selecione...</option>
                              {destLocations.list.filter(l => l.id !== item.fromLocationId).map(l => (
                                <option key={l.id} value={l.id}>
                                  {locLabel(l)}{l.id === generalLoc?.id ? ' — estoque do fracionamento' : ''}
                                </option>
                              ))}
                            </select>
                            {destLocations.unmapped && (
                              <p className="mt-1 text-xs text-amber-600">Nenhuma localização vinculada a esse armazém — mostrando todas.</p>
                            )}
                          </div>

                          {item.source === 'pallet' ? (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-700">Lote do palete</label>
                              <p className="rounded-lg border border-gray-100 bg-white px-3 py-2 font-mono text-sm text-gray-600">
                                {pallet?.lotNumber ?? '—'}
                              </p>
                            </div>
                          ) : item.fromLocationId && (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-700">
                                Lote (FIFO — mais antigo primeiro)
                              </label>
                              {fifoLots.length > 0 ? (
                                <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                                  value={item.lotId} onChange={e => setItem(idx, { lotId: e.target.value })}>
                                  <option value="">Sem lote</option>
                                  {fifoLots.map((l, li) => (
                                    <option key={l.lotId} value={l.lotId}>
                                      {li === 0 ? '★ ' : ''}{l.lotNumber} — {l.quantity} un — {new Date(l.receivedAt).toLocaleDateString('pt-BR')}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <p className="text-xs text-gray-400 mt-1">Sem lotes rastreados nessa localização</p>
                              )}
                            </div>
                          )}

                          {item.source === 'pallet' ? (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-700">
                                Caixas {pallet ? `(disponível: ${pallet.boxes})` : ''}
                              </label>
                              <input type="number" min={1} max={pallet?.boxes ?? undefined}
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-right"
                                value={item.boxesRequested}
                                onChange={e => setItem(idx, { boxesRequested: parseInt(e.target.value) || 1 })} />
                              <p className="mt-1 text-xs text-gray-500">
                                {upc > 0
                                  ? `= ${item.boxesRequested * upc} un (${upc} un/cx) — caixa fechada`
                                  : 'Produto sem "unidades por caixa" no cadastro'}
                              </p>
                            </div>
                          ) : (
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-700">Quantidade (un)</label>
                              <input type="number" min={1} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-right"
                                value={item.quantityRequested} onChange={e => setItem(idx, { quantityRequested: parseInt(e.target.value) || 1 })} />
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {create.error && <p className="text-sm text-red-600">{(create.error as any)?.response?.data?.detail ?? (create.error as any)?.response?.data?.title ?? 'Erro ao criar'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button loading={create.isPending} disabled={!canCreate} onClick={() => create.mutate()}>Criar transferência</Button>
          </div>
        </div>
      </Modal>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : transfers.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhuma transferência registrada</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Armazém origem</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Justificativa</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-5 py-3 text-left font-medium text-gray-600">Data</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transfers.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-gray-700">{t.fromWarehouseName ?? '—'}</td>
                    <td className="px-5 py-3 text-gray-700 max-w-xs truncate">{t.reason}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusVariant[t.status] ?? 'default'}>{statusLabel[t.status] ?? t.status}</Badge>
                    </td>
                    <td className="px-5 py-3 text-gray-500">{new Date(t.createdAt).toLocaleDateString('pt-BR')}</td>
                    <td className="px-5 py-3">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/transfers/${t.id}`)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
